import CreatePlayersOffered from '@/components/features/contacts/players_offered/create';
import UpdatePlayersOffered from '@/components/features/contacts/players_offered/update';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ROUTES_CONFIG } from '@/config/routes.config';
import { LANGUAGES } from '@/constants/languages';
import { formatDate } from '@/lib/date';
import { useTRPC } from '@/trpc';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Icon from 'lucide-react'
import { Link } from 'react-router-dom';

interface Props {
    contact_id: string;
}

const PlayersOffered = ({ contact_id }: Props) => {
  const queryClient = useQueryClient();
  const { trpc } = useTRPC();

  const { data:players } = useQuery({
    queryKey: ['players-offered', contact_id],
    queryFn: async () => {
      const players = await trpc.contacts.playersOffered.search.mutate({
        contact_id: contact_id
      })
      return players;
    }
  })

  return (
    <div className='h-full overflow-auto'>
      <div className='flex flex-col justify-between h-full gap-4'>
        <div className='flex items-center justify-between gap-4'>
          <div>
            <Input placeholder="Zoek speler" />
          </div>

          <CreatePlayersOffered 
            contact_id={contact_id}
            onCreate={()=>{
              queryClient.invalidateQueries(['players-offered', contact_id])
            }}
            trigger={
              <Button variant='bright'>
                <Icon.Plus />
                Nieuwe speler
              </Button>
            }
          />
          
        </div>

        <div className='h-full overflow-auto'>
          <Table divClassname='overflow-y-scroll h-full' className='relative overflow-clip'>
            <TableHeader className='sticky top-0 bg-card'>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead>Club</TableHead>
                <TableHead>Positie</TableHead>
                <TableHead>Rol positie</TableHead>
                <TableHead>Telefoon</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Transfer bedrag</TableHead>
                <TableHead>Salarisverwachting</TableHead>
                <TableHead>Talen</TableHead>
                <TableHead>Notitie</TableHead>
                <TableHead>Url</TableHead>
                <TableHead>Extra info</TableHead>
                <TableHead>Gewijzigd op</TableHead>
                <TableHead>Aangemaakt op</TableHead>
                <TableHead className='sticky right-0 z-10 bg-card'></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {players?.map((player)=>(
                <TableRow key={player.id}>
                  <TableCell>
                    <Link 
                      to={ROUTES_CONFIG.APP.CONTACTS.DETAIL.OFFERED_PLAYER_ID.$buildPath({ params:{ contact_id: contact_id, offered_player_id: player.id }})}
                      className="font-medium hover:underline"
                    >
                      {player?.mongo_player?.name}
                    </Link>
                  </TableCell>
                  <TableCell>{player?.mongo_club?.name}</TableCell>
                  <TableCell>{player?.position}</TableCell>
                  <TableCell>{player?.role_position}</TableCell>
                  <TableCell>{player?.telephone}</TableCell>
                  <TableCell>{player?.email}</TableCell>
                  <TableCell>{player?.transfer_amount}</TableCell>
                  <TableCell>{player?.salary_expectation}</TableCell>
                  <TableCell>{player?.languages?.map(l => LANGUAGES.find(lang=>lang.value === l)?.label).join(', ')}</TableCell>
                  <TableCell>{player?.note}</TableCell>
                  <TableCell>{player?.external_url}</TableCell>
                  <TableCell>{player?.extra_info}</TableCell>
                  <TableCell>{formatDate(player?.updated_at)}</TableCell>
                  <TableCell>{formatDate(player?.created_at)}</TableCell>
                  <TableCell className='sticky right-0 z-10 bg-card'>
                    <div className='flex items-center justify-end'>
                      <DropdownMenu>
                        <DropdownMenuTrigger>
                          <Icon.MoreVertical className="w-4 h-4 text-muted-foreground" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <UpdatePlayersOffered
                            player_offered_id={player.id}
                            onUpdate={()=>{
                              queryClient.invalidateQueries(['players-offered', contact_id])
                            }}
                            defaultValues={{
                              offered_by_id: player.offered_by_id,
                              mongo_player_id: player.mongo_player_id ?? undefined,
                              position: player.position ?? undefined,
                              role_position: player.role_position ?? undefined,
                              telephone: player.telephone ?? undefined,
                              email: player.email ?? undefined,
                              transfer_amount: player.transfer_amount ? parseInt(player.transfer_amount) : undefined,
                              salary_expectation: player.salary_expectation ? parseInt(player.salary_expectation) : undefined,
                              languages: player.languages ?? undefined,
                              note: player.note ?? undefined,
                              external_url: player.external_url ?? undefined,
                              extra_info: player.extra_info ?? undefined,
                              company: player.company ?? undefined,
                            }}
                            trigger={
                              <DropdownMenuItem onSelect={(e)=>{e.preventDefault()}}>
                                <Icon.Edit className='w-4 h-4'/>
                                Bewerken
                              </DropdownMenuItem>
                            }
                          />
                          <DropdownMenuItem className='text-destructive hover:!text-destructive/80'>
                            <Icon.Trash className='w-4 h-4 text-destructive'/>
                            Verwijderen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}

export default PlayersOffered